import { Routes } from '@angular/router';
import { customerBootstrapGuard } from './core/guards/customer-bootstrap.guard';
import { authGuard } from './core/guards/auth.guard';
import { policyProvisionedGuard } from './core/guards/policy-provisioned.guard';
import { portalConfigGuard } from './core/guards/portal-config.guard';
import { formCanDeactivateGuard } from './core/guards/form-can-deactivate.guard';
import { previewGuard } from './core/guards/preview.guard';
import { disclosureModuleGuard } from './features/form/guards/disclosure-module.guard';

/**
 * Shared child routes for bootstrap-guarded group.
 *
 * Mounted at path-based parent with customer ID prefix:
 *  - `':customerId'` — path-based deployments: `/ACME/login`, `/ACME/home`, etc.
 *
 * All routes must include the customer ID in the path for clear, shareable URLs.
 */
const BOOTSTRAP_CHILDREN: Routes = [
  {
    path: 'portal-preview',
    canActivate: [previewGuard],
    loadComponent: () =>
      import('./features/home/home-page.component').then((m) => m.HomePageComponent),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/components/login-page/login-page.component').then(
        (m) => m.LoginPageComponent
      ),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/pages/forgot-password/forgot-password-page.component').then(
        (m) => m.ForgotPasswordPageComponent
      ),
  },
  {
    path: 'validate-access-code',
    loadComponent: () =>
      import('./features/auth/pages/validate-access-code/validate-access-code-page.component').then(
        (m) => m.ValidateAccessCodePageComponent
      ),
  },
  {
    path: 'change-password',
    loadComponent: () =>
      import('./features/auth/pages/change-password/change-password-page.component').then(
        (m) => m.ChangePasswordPageComponent
      ),
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./features/registration/pages/register/register-page.component').then(
        (m) => m.RegisterPageComponent
      ),
  },

  // ─── Auth-guarded group (requires active JWT session) ─────────────────────
  {
    path: '',
    canActivate: [authGuard, portalConfigGuard],
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/home/home-page.component').then((m) => m.HomePageComponent),
      },
      {
        path: 'faq',
        loadComponent: () =>
          import('./features/faq/components/faq-page/faq-page.component').then(
            (m) => m.FaqPageComponent
          ),
      },
      {
        // Disclosures page (Replit parity). The component lives under features/my-compliance
        // but the employee-facing route + nav label is "Disclosures".
        path: 'disclosures',
        canActivate: [disclosureModuleGuard],
        loadComponent: () =>
          import('./features/my-compliance/my-compliance-page.component').then(
            (m) => m.MyCompliancePageComponent
          ),
      },
      {
        // Back-compat: the route was previously /my-compliance. Redirect to /disclosures
        // so any bookmarks / deep links keep working.
        path: 'my-compliance',
        redirectTo: 'disclosures',
        pathMatch: 'full',
      },
      {
        // Policy library (Story 21) — placeholder list page. Nav order: Home → Policies → My Compliance.
        path: 'policies',
        canActivate: [policyProvisionedGuard],
        loadComponent: () =>
          import('./features/policy-library/policy-library-page.component').then(
            (m) => m.PolicyLibraryPageComponent
          ),
      },
      {
        // Policy detail page (Story 22 / Feature 023) at /policies/:id.
        // Opened from a My Compliance task-row click; the assignment id is passed via
        // the `?assignmentId=` query param so the read-view + acknowledge modal can load.
        path: 'policies/:id',
        canActivate: [policyProvisionedGuard],
        loadComponent: () =>
          import('./features/policy-attestation/policy-detail-page.component').then(
            (m) => m.PolicyDetailPageComponent
          ),
      },
      // ─── Form routes ──────────────────────────────────────────────────────
      // Native Form.io renderer — replaces the iframe approach.
      {
        path: 'form/:disclosureId',
        canActivate: [disclosureModuleGuard],
        canDeactivate: [formCanDeactivateGuard],
        loadComponent: () =>
          import(
            './features/form/pages/disclosure-form-page/disclosure-form-page.component'
          ).then((m) => m.DisclosureFormPageComponent),
      },
      {
        path: 'submission/:id',
        loadComponent: () =>
          import('./features/submission/submission-page.component').then(
            (m) => m.SubmissionPageComponent
          ),
      },
      {
        // Completed forms are now handled by the same DisclosureFormPageComponent
        // via ?formResponseId query param. Redirect legacy route.
        path: 'completed-form/:formResponseId',
        canActivate: [disclosureModuleGuard],
        canDeactivate: [
          (component: { canDeactivate?: () => boolean }) =>
            component.canDeactivate?.() ?? true,
        ],
        loadComponent: () =>
          import(
            './features/form/pages/disclosure-form-page/disclosure-form-page.component'
          ).then((m) => m.DisclosureFormPageComponent),
      },
      {
        path: 'quizzes',
        loadComponent: () =>
          import('./features/quiz/quiz-page.component').then(
            (m) => m.QuizPageComponent
          ),
      },
    ],
  },
];

export const routes: Routes = [
  // Root path: run the bootstrap guard so subdomain-based entry is handled automatically.
  // The guard always produces a UrlTree for directEntry routes (to /login or Keycloak),
  // so the loadComponent below is a never-rendered fallback required by the router.
  {
    path: '',
    pathMatch: 'full',
    canActivate: [customerBootstrapGuard],
    data: { directEntry: true },
    loadComponent: () =>
      import('./features/access-id/access-id-page.component').then(
        (m) => m.CustomerAccessIdPageComponent
      ),
  },

  // ─── Public certificate verification: /verify/:code ──────────────────────
  // AllowAnonymous on the backend — no auth, no customer-bootstrap needed.
  // Replit parity: verify.tsx — dark-navy gradient page, valid/expired/not-found states.
  {
    path: 'verify/:code',
    loadComponent: () =>
      import('./features/quiz/components/certificate-verify/certificate-verify-page.component').then(
        (m) => m.CertificateVerifyPageComponent
      ),
  },

  // ─── Unguarded entry / error routes ───────────────────────────────────────
  {
    // Flat validate-access-code entry: email link pattern
    // /employee-view/validate-access-code?email=...&id=...&customerId=DEMO
    path: 'employee-view/validate-access-code',
    loadComponent: () =>
      import('./features/auth/pages/validate-access-code/validate-access-code-page.component').then(
        (m) => m.ValidateAccessCodePageComponent
      ),
  },
  {
    // Flat account-validation landing route: the validation email links to
    // /redirect?cid=...&etype=login&email=...&id=...
    // Unguarded on purpose — `cid` is not a tenant source customerBootstrapGuard
    // understands, so a guarded route would divert the email link to /access-id.
    path: 'redirect',
    loadComponent: () =>
      import('./features/registration/pages/redirect-landing/redirect-landing-page.component').then(
        (m) => m.RedirectLandingPageComponent
      ),
  },
  {
    path: 'access-id',
    loadComponent: () =>
      import('./features/access-id/access-id-page.component').then(
        (m) => m.CustomerAccessIdPageComponent
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/pages/sso-callback/sso-callback-page.component').then(
        (m) => m.SsoCallbackPageComponent
      ),
  },
  {
    path: 'error',
    loadComponent: () =>
      import('./core/components/error-page/error-page.component').then(
        (m) => m.ErrorPageComponent
      ),
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./core/components/not-found-page/not-found-page.component').then(
        (m) => m.NotFoundPageComponent
      ),
  },

  // ─── Public admin route: /admin-dashboard/employee-portal-configurations ───
  // Must be declared BEFORE the empty-path bootstrap group so the
  // customerBootstrapGuard never intercepts this URL.
  {
    path: 'admin-dashboard/employee-portal-configurations',
    loadComponent: () =>
      import('./features/admin-dashboard/admin-dashboard.component').then(
        (m) => m.AdminDashboardComponent
      ),
  },

  // ─── Admin: Portal Configuration (/admin-dashboard/customers/:customerId/portal-configuration) ──
  // Mount admin portal routes under a static parent segment so they are matched before
  // dynamic ':customerId' routes and are never interpreted as a customer-prefixed URL.
  {
    path: 'admin-dashboard/customers',
    loadChildren: () =>
      import('./features/admin-dashboard/portal-config.routes').then(
        (m) => m.PORTAL_CONFIG_ROUTES
      ),
  },

  // ─── Flat subdomain-based group: /login, /home, /form/:id, /faq ──────────
  // When customer is identified by subdomain or localStorage, routes don't need customer ID prefix.
  // This group must come BEFORE the :customerId routes so flat paths match here first.
  {
    path: '',
    canActivate: [customerBootstrapGuard],
    children: BOOTSTRAP_CHILDREN,
  },

  // ─── Path-based entry: bare /employee-view/customers/ACME → redirect to /employee-view/customers/ACME/login or home ─
  // pathMatch: 'full' ensures only the single-segment URL matches here;
  // /employee-view/customers/ACME/login, /ACME/home etc. fall through to the children group below.
  {
    path: 'employee-view/customers/:customerId',
    pathMatch: 'full',
    canActivate: [customerBootstrapGuard],
    data: { directEntry: true },
    loadComponent: () =>
      import('./features/access-id/access-id-page.component').then(
        (m) => m.CustomerAccessIdPageComponent
      ),
  },

  // ─── Path-based group: /employee-view/customers/ACME/login, /ACME/home, etc. ─
  // Mounts the same child route tree as the flat subdomain group so any new
  // route is automatically available under both URL structures.
  {
    path: 'employee-view/customers/:customerId',
    canActivate: [customerBootstrapGuard],
    children: BOOTSTRAP_CHILDREN,
  },

  // Catch-all — redirect unknown paths to not-found
  { path: '**', redirectTo: 'not-found' },
];
